import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase-config';
import { useAuth } from '../context/AuthContext';

const EFFECTIVE_DATE = 'March 1, 2026';
const COMPANY = 'RAMDESIGNWORKS';
const SITE = 'theoperatormethod.com';
const EMAIL = 'support@theoperatormethod.com';

export default function BetaTosPage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  async function handleAccept() {
    if (!currentUser) { navigate('/login'); return; }
    setAccepting(true);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        betaTosAccepted: true,
        betaTosAcceptedAt: serverTimestamp(),
      });
      setAccepted(true);
      setTimeout(() => navigate('/v2-course'), 1200);
    } catch (err) {
      console.error('Failed to record TOS acceptance:', err);
      setAccepting(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#0a0e1a',
      fontFamily: "'Inter', -apple-system, sans-serif",
      color: '#f0f4ff',
    }}>
      {/* Nav */}
      <div style={{
        padding: '20px 40px',
        borderBottom: '1px solid #1e2a3a',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <button
          onClick={() => navigate('/')}
          style={{ background: 'none', border: 'none', color: '#4f8ef7', cursor: 'pointer', fontSize: '11px', letterSpacing: '0.25em', fontWeight: 700 }}
        >
          THE OPERATOR METHOD
        </button>
        <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.18em', color: 'rgba(0,180,255,0.5)', textTransform: 'uppercase' }}>
          Beta Program
        </span>
      </div>

      <div style={{ maxWidth: '760px', margin: '0 auto', padding: '60px 24px 80px' }}>

        <p style={{ fontSize: '11px', letterSpacing: '0.25em', color: '#4f8ef7', textTransform: 'uppercase', marginBottom: '16px' }}>Legal — Beta Program</p>
        <h1 style={{ fontSize: '32px', fontWeight: 700, margin: '0 0 8px', color: '#f0f4ff' }}>Beta Tester Data &amp; Logging Agreement</h1>
        <p style={{ color: '#4a5568', fontSize: '14px', marginBottom: '48px' }}>Effective date: {EFFECTIVE_DATE}</p>

        <Section title="1. Who This Agreement Applies To">
          <p>This Beta Tester Data &amp; Logging Agreement ("Agreement") applies exclusively to individuals granted beta access to The Operator Method platform operated by <strong>{COMPANY}</strong>. By using the platform as a beta tester — including accessing course modules, interacting with AI features, or submitting feedback — you agree to the terms set forth in this Agreement in addition to the general <a href="/terms" style={linkStyle}>Terms of Service</a>.</p>
          <p>Your continued use of the platform after receiving beta access constitutes your full and voluntary consent to all data collection and logging practices described below.</p>
        </Section>

        <Section title="2. What We Log and Why">
          <p>As a beta tester, the following categories of interaction data are automatically recorded for the sole purpose of improving the platform before public launch:</p>
          <ul style={listStyle}>
            <li><strong>AI Conversation Transcripts</strong> — The complete content of every exchange between you and any AI feature, including the diagnostic chatbot, the course AI instructor, and all other AI-powered tools on the platform.</li>
            <li><strong>Session Metadata</strong> — Session identifiers, timestamps, exchange count, lesson completion events, and assessed skill level markers.</li>
            <li><strong>Feedback Responses</strong> — Any structured survey responses or free-text feedback you submit through the platform.</li>
            <li><strong>Usage Patterns</strong> — Pages visited, features interacted with, and time spent in session (no keystroke logging or off-platform tracking).</li>
            <li><strong>Platform Errors and Anomalies</strong> — Any technical errors, unusual inputs, or flagged interactions that occur during your session.</li>
          </ul>
          <p>Logging is automatic and continuous throughout your beta session. You cannot opt out of logging while participating in the beta program. If you do not consent to this logging, do not use the beta platform.</p>
        </Section>

        <Section title="3. Automated PDF Session Records">
          <p>All beta tester AI conversation sessions are automatically compiled into a PDF session record each time you interact with the platform. These records:</p>
          <ul style={listStyle}>
            <li>Are generated automatically and stored in a secured cloud storage environment.</li>
            <li>Are updated and replaced each time you return to an existing session, so only the most current version of each session is retained at any given time.</li>
            <li>Are accessible to authorized {COMPANY} personnel for internal development review only.</li>
            <li>Will not be shared publicly or with third parties outside of those described in Section 5.</li>
          </ul>
        </Section>

        <Section title="4. How Your Data Is Used">
          <p>All data collected under this Agreement is used exclusively for:</p>
          <ul style={listStyle}>
            <li>Evaluating and improving AI prompt design, response quality, and conversation flow</li>
            <li>Identifying usability gaps, friction points, and bugs in the platform</li>
            <li>Refining the course curriculum and exercise structure based on real tester behavior</li>
            <li>Calibrating AI difficulty, pacing, and personalization systems</li>
          </ul>
          <p>Your data will not be used for advertising, sold to third parties, or used to build profiles for any purpose outside of this platform's internal development.</p>
        </Section>

        <Section title="5. Data Storage and Security">
          <p>All beta interaction data is stored within Google Firebase (Firestore and Firebase Storage), which operates under Google's enterprise-grade security infrastructure, including:</p>
          <ul style={listStyle}>
            <li>Encryption at rest and in transit using industry-standard TLS/AES-256 protocols</li>
            <li>Role-based access controls that restrict data access to authorized {COMPANY} administrators only</li>
            <li>Firestore Security Rules that prevent public or unauthorized client access to any user data collections</li>
            <li>Firebase Storage access tokens required for all PDF file access</li>
          </ul>
          <p>Data is retained for the duration of the beta program and for a reasonable period afterward for internal review, not to exceed 24 months from collection. Upon request, we will delete your individual beta data records. Contact us at <a href={`mailto:${EMAIL}`} style={linkStyle}>{EMAIL}</a>.</p>
          <p>All data handling practices comply with applicable U.S. federal and state privacy and data protection laws, including, where applicable, the California Consumer Privacy Act (CCPA). If you are located in the European Economic Area (EEA), you acknowledge that data may be transferred to and processed in the United States under appropriate safeguards.</p>
        </Section>

        <Section title="6. Your Rights as a Beta Tester">
          <p>You may at any time:</p>
          <ul style={listStyle}>
            <li><strong>Request a copy</strong> of any data we hold about your beta sessions by contacting <a href={`mailto:${EMAIL}`} style={linkStyle}>{EMAIL}</a></li>
            <li><strong>Request deletion</strong> of your beta data records — we will fulfill deletion requests within 30 days</li>
            <li><strong>Withdraw from the beta program</strong> at any time, which will result in termination of your beta access and cessation of future logging</li>
          </ul>
          <p>Withdrawal from the beta program does not constitute a refund claim, as beta access is granted at no charge under a separate arrangement.</p>
        </Section>

        <Section title="7. Prohibited Use and Enforcement">
          <p>Beta program access does not alter or waive any of the prohibitions in the general <a href="/terms" style={linkStyle}>Terms of Service</a>, including prohibitions on illegal use, fraud, and privacy violations. Interactions flagged as malicious, abusive, or in violation of the Terms will be logged and may result in immediate termination of beta access.</p>
          <p>You agree that data collected through prohibited interactions may be reviewed, preserved, and disclosed to appropriate authorities as required by law.</p>
          <p><strong>Security Doctrine.</strong> All AI enforcement mechanisms governing this beta program — including malicious input detection, session fingerprinting, and account bans — derive from the <a href="/security-doctrine" style={linkStyle}>AI Security Doctrine, Version 1.0</a>. Enforcement follows a four-level escalation protocol: (1) Restricted Response — the exchange is logged and the AI redirects; (2) Session Suspension — the session is terminated and the fingerprint is flagged; (3) Account Ban — all AI access is permanently revoked; (4) Legal Referral — session data, IP records, and fingerprints are preserved and may be disclosed to law enforcement.</p>
        </Section>

        <Section title="8. Consent by Use">
          <p>By clicking "I Accept" below, or by using the beta platform following receipt of beta credentials, you confirm that:</p>
          <ul style={listStyle}>
            <li>You have read and understood this Agreement</li>
            <li>You consent to the collection, logging, storage, and use of your interaction data as described</li>
            <li>You are at least 18 years of age</li>
            <li>Your consent is voluntary and informed</li>
          </ul>
        </Section>

        <Section title="9. Contact">
          <p>
            {COMPANY}<br />
            Email: <a href={`mailto:${EMAIL}`} style={linkStyle}>{EMAIL}</a><br />
            Website: <a href={`https://${SITE}`} style={linkStyle}>{SITE}</a>
          </p>
        </Section>

        {/* Accept block */}
        <div style={{
          marginTop: '48px',
          padding: '32px',
          background: 'rgba(0,180,255,0.05)',
          border: '1px solid rgba(0,180,255,0.2)',
          borderRadius: '16px',
          textAlign: 'center',
        }}>
          {accepted ? (
            <div style={{ color: '#34d399', fontWeight: 700, fontSize: '18px' }}>
              ✓ Agreement accepted. Taking you to the course…
            </div>
          ) : (
            <>
              <p style={{ color: '#7ab8d4', fontSize: '14px', lineHeight: 1.8, marginBottom: '24px', margin: '0 0 24px' }}>
                By clicking below, you confirm that you have read and agree to the terms of this Beta Tester Data &amp; Logging Agreement. Your use of the platform constitutes ongoing consent to these terms.
              </p>
              <button
                onClick={handleAccept}
                disabled={accepting}
                style={{
                  padding: '15px 48px',
                  backgroundColor: accepting ? 'rgba(0,180,255,0.3)' : '#00d4ff',
                  color: '#001833',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: 800,
                  cursor: accepting ? 'not-allowed' : 'pointer',
                  width: '100%',
                  maxWidth: '360px',
                }}
              >
                {accepting ? 'Recording your agreement…' : 'I Accept — Enter the Course →'}
              </button>
              <p style={{ color: '#2a3550', fontSize: '12px', marginTop: '16px' }}>
                Declining means you cannot participate in the beta program.
              </p>
            </>
          )}
        </div>

        <div style={{ borderTop: '1px solid #1e2a3a', paddingTop: '32px', marginTop: '48px', display: 'flex', gap: '24px' }}>
          <button onClick={() => navigate('/terms')} style={{ background: 'none', border: 'none', color: '#4f8ef7', cursor: 'pointer', fontSize: '14px', padding: 0 }}>General Terms of Service →</button>
          <button onClick={() => navigate('/privacy')} style={{ background: 'none', border: 'none', color: '#4f8ef7', cursor: 'pointer', fontSize: '14px', padding: 0 }}>Privacy Policy →</button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: '40px' }}>
      <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#f0f4ff', margin: '0 0 16px', paddingBottom: '8px', borderBottom: '1px solid #1e2a3a' }}>{title}</h2>
      <div style={{ color: '#94a3b8', lineHeight: 1.8, fontSize: '15px' }}>{children}</div>
    </div>
  );
}

const listStyle = { paddingLeft: '20px', lineHeight: 2, margin: '8px 0' };
const linkStyle = { color: '#4f8ef7', textDecoration: 'none' };
